export interface AccessInfoNoticeProps {
	/** "global" = Company Wide Access, "specific" = Specific Access (CONTRACT §11's `mode`). */
	mode: "global" | "specific";
}

/**
 * A6 — VERBATIM copy, byte-for-byte from the ticket's "Required copy" section.
 * Do NOT normalise punctuation, casing or spacing. Do NOT apply the C4
 * Global -> Company Wide terminology sweep to this prose — CONTRACT §10.3
 * carves this exact paragraph out: it is frozen ticket copy (A6), which wins
 * over C4 for this string specifically. C4 governs standalone labels/headings
 * only (see `AccessModeSelector`'s "Company Wide Access" card heading).
 *
 * Note deliberately-as-written details that a future edit must not "fix":
 * - the company-wide copy's own lowercase mid-sentence "company-wide"
 * - lowercase "rfx" in the company-wide copy vs capitalised "RFx" in the
 *   specific copy
 * - a hyphen (not an em-dash) after "cascades"
 * - no trailing full stop on the company-wide copy
 */
const COMPANY_WIDE_COPY =
	"Granting company-wide access to a user will provide them with full access for the selected role across all portfolios, projects and rfx existing now and those added in the future";

/**
 * AUTHOR AMENDMENT (2026-09-24, author item 3.3) — A6/CONTRACT §10.3 froze this exact sentence
 * on the MODE step's Specific card. The author has since moved where it lives (now the TOP of
 * the level-selection/picker step — see `ScopePicker`'s own `data-testid="scope-cascade-notice"`)
 * and replaced it on this screen with the short "click next" prompt below, because the picker no
 * longer renders on this same step (it moved to a later step in D8/D10's flow, so "from the
 * options below" pointed at nothing). The author's instruction wins over the frozen copy per the
 * coordinator's recorded amendment; this string is exported so `ScopePicker` renders the exact
 * same verbatim text it always was, just relocated, not reworded.
 */
export const CASCADE_COPY =
	"Please select different levels of access for this user from the options below. Security access cascades - For example, if you pick a portfolio they have access to all projects and RFx within that Portfolio.";

const SPECIFIC_MODE_COPY = "Click next to select access levels";

// Renders the required-copy info block for the current Add-flow mode (D8-T1, A6).
export const AccessInfoNotice = ({ mode }: AccessInfoNoticeProps) => (
	<div
		className="rounded-md border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm leading-relaxed text-blue-900"
		data-testid="access-info-notice"
		role="note"
	>
		{mode === "global" ? COMPANY_WIDE_COPY : SPECIFIC_MODE_COPY}
	</div>
);
